import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceRoles, type WorkspaceAccess, type WorkspaceRole } from '@pm/shared';
import { api } from '@/lib/api';
import { homeBody, homeLists, keys, type Home } from '@/lib/query';

export interface WorkspaceListItem {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  /** Any staff member can find it and join; closed ones are invite-only. */
  isOpen: boolean;
  /** How the signed-in person stands in it: their role, or 'visitor' in an open one they have not joined. */
  access: WorkspaceAccess;
  /** The organization it sits in, and the organization folder (null: its top level). */
  organizationId: string | null;
  folderId: string | null;
  _count: { boards: number; members: number };
  members: { role: WorkspaceRole; user: { id: string; name: string; avatarUrl: string | null } }[];
}

export interface BoardListItem {
  id: string;
  name: string;
  description: string | null;
  isPrivate: boolean;
  folderId: string | null;
  _count: { items: number; groups: number };
}

export interface FolderItem {
  id: string;
  parentId: string | null;
  name: string;
}

export const useWorkspaces = () =>
  useQuery({ queryKey: keys.workspaces, queryFn: () => api.get<WorkspaceListItem[]>('/workspaces') });

export const useBoards = (workspaceId: string) =>
  useQuery({
    queryKey: keys.boards(workspaceId),
    queryFn: () => api.get<BoardListItem[]>(`/workspaces/${workspaceId}/boards`),
    enabled: Boolean(workspaceId),
  });

/**
 * Always into an organization. A folder and someone else as owner are for
 * whoever manages organizations; the API refuses them to anyone else.
 */
export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; description?: string; color?: string; icon?: string;
      organizationId: string; folderId?: string | null; ownerId?: string;
    }) => api.post<WorkspaceListItem>('/workspaces', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.workspaces });
      void qc.invalidateQueries({ queryKey: keys.organizations });
    },
  });
}

export function useCreateBoard(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; folderId?: string | null; isPrivate?: boolean }) =>
      api.post<BoardListItem>('/boards', { ...body, workspaceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.boards(workspaceId) }),
  });
}

export const useFolders = (workspaceId: string) =>
  useQuery({
    queryKey: keys.folders(workspaceId),
    queryFn: () => api.get<FolderItem[]>(`/workspaces/${workspaceId}/folders`),
    enabled: Boolean(workspaceId),
  });

/** A workspace's folders, or an organization's own. */
export function useFolderActions(home: Home) {
  const qc = useQueryClient();
  // A deleted folder hands what it held to its parent, so all of it changes.
  const invalidate = () => {
    for (const key of Object.values(homeLists(home))) void qc.invalidateQueries({ queryKey: key });
    if (home.workspaceId) void qc.invalidateQueries({ queryKey: keys.boards(home.workspaceId) });
    else void qc.invalidateQueries({ queryKey: keys.organizationWorkspaces(home.organizationId ?? '') });
  };

  return {
    create: useMutation({
      mutationFn: (body: { name: string; parentId: string | null }) =>
        api.post<FolderItem>('/folders', { ...body, ...homeBody(home) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: { name?: string; parentId?: string | null } }) =>
        api.put<FolderItem>(`/folders/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/folders/${id}`),
      onSuccess: invalidate,
    }),
    moveBoard: useMutation({
      mutationFn: ({ boardId, folderId }: { boardId: string; folderId: string | null }) =>
        api.put(`/boards/${boardId}`, { folderId }),
      onSuccess: invalidate,
    }),
  };
}

/** Every role but owner: a workspace changes hands only through "Make owner". */
export type MemberRole = Exclude<WorkspaceRole, 'owner'>;
export const memberRoles = workspaceRoles.filter((r): r is MemberRole => r !== 'owner');

export interface WorkspaceMember {
  workspaceId: string;
  userId: string;
  role: WorkspaceRole;
  /** No email for someone only looking around an open workspace. */
  user: { id: string; name: string; email: string | null; avatarUrl: string | null };
}

export const useMembers = (workspaceId: string) =>
  useQuery({
    queryKey: ['workspaces', workspaceId, 'members'],
    queryFn: () => api.get<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`),
    enabled: Boolean(workspaceId),
  });

/**
 * Everyone who can open the workspace - members plus admins and HR - for the
 * @ list in updates and descriptions. Not the same as `useMembers`, which is
 * only the people added to it.
 */
export const useMentionable = (workspaceId: string) =>
  useQuery({
    queryKey: ['workspaces', workspaceId, 'mentionable'],
    queryFn: () => api.get<{ id: string; name: string; avatarUrl: string | null }[]>(`/workspaces/${workspaceId}/mentionable`),
    enabled: Boolean(workspaceId),
    staleTime: 5 * 60_000,
  });

export function useWorkspaceActions(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.workspaces });
    void qc.invalidateQueries({ queryKey: ['workspaces', workspaceId] });
  };

  return {
    update: useMutation({
      mutationFn: (body: Record<string, unknown>) => api.put(`/workspaces/${workspaceId}`, body),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: () => api.delete(`/workspaces/${workspaceId}`),
      onSuccess: invalidate,
    }),
    addMember: useMutation({
      mutationFn: (body: { userId: string; role: MemberRole }) =>
        api.post(`/workspaces/${workspaceId}/members`, body),
      onSuccess: invalidate,
    }),
    removeMember: useMutation({
      mutationFn: (userId: string) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),
      onSuccess: invalidate,
    }),
    /** "Make owner": the old owner stays on as an admin, so the session's roles change too. */
    transferOwnership: useMutation({
      mutationFn: (userId: string) => api.post(`/workspaces/${workspaceId}/transfer`, { userId }),
      onSuccess: () => {
        invalidate();
        void qc.invalidateQueries({ queryKey: keys.session });
      },
    }),
    /** Everyone in a department joins with one role; members already in keep theirs. */
    addDepartment: useMutation({
      mutationFn: (body: { departmentId: string; role: MemberRole }) =>
        api.post<{ added: number; alreadyMembers: number; department: string }>(
          `/workspaces/${workspaceId}/members/department`, body),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Joining an open workspace, or leaving one. Either changes what the person
 * may do in every list and page of it, so all of it is asked again.
 */
export function useMembership() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.workspaces });
    void qc.invalidateQueries({ queryKey: keys.session });
    void qc.invalidateQueries({ queryKey: ['boards'] });
    void qc.invalidateQueries({ queryKey: ['docs'] });
  };
  return {
    join: useMutation({
      mutationFn: (workspaceId: string) => api.post(`/workspaces/${workspaceId}/join`),
      onSuccess: refresh,
    }),
    leave: useMutation({
      mutationFn: (workspaceId: string) => api.post(`/workspaces/${workspaceId}/leave`),
      onSuccess: refresh,
    }),
  };
}
